<template>
    <slot/>
</template>

<script setup lang="ts">
import { watch, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useWebInquiryActions } from '@/project/app/blocks/webinquiries/useWebInquiryActions';
import webInquirySubscribeOverlayService from '@/project/modals/overlays/WebInquirySubscribeOverlay.service';
import formRequestOverlayService, { FormRequestType } from '@/project/modals/overlays/FormRequestOverlay.service';
import { UrlQueryKey } from '@/core/urlQueryKey';
import authService from '@/core/auth/auth.service';
import { router } from '@/router';
import loginFormOverlayService from '@/project/modals/overlays/LoginFormOverlay.service';
import loggingService from '@/core/logging.service';

const route = useRoute();
let pendingTimer: ReturnType<typeof setTimeout> | undefined;

function resolveLoadTimeDelay(loadTime?: string): number {
    switch (loadTime?.split(' ').shift()) {
        case 'Medium': return 2000;
        case 'Slow': return 4000;
        default: return 0;
    }
}

async function checkForWebInquiryForm() {
    const id = route.query[UrlQueryKey.WebInquiry];
    if (!id || typeof id !== 'string') {
        return;
    }

    if (authService.isLoggedIn()) {
        const pathAtTriggerTime = route.path;
        const cleanedQuery = { ...route.query };
        delete cleanedQuery[UrlQueryKey.WebInquiry];
        router.replace({ query: cleanedQuery }).catch(() => {});

        try {
            const { loadWebInquiryActions } = useWebInquiryActions();
            const block = await loadWebInquiryActions(id);
            const delay = resolveLoadTimeDelay(block.loadTime ?? undefined);

            pendingTimer = setTimeout(() => {
                if (route.path !== pathAtTriggerTime) {
                    return;
                }
                webInquirySubscribeOverlayService.show(block);
            }, delay);
        } catch {
        }
    } else {
        const path = router.currentRoute.value.path;
        const query = router.currentRoute.value.query
            ? Object.entries(router.currentRoute.value.query)
                .map(([key, value]) => `${key}=${value}`)
                .join('&')
            : '';

        await loginFormOverlayService.show('login', `${path}?${query}`);
        if (authService.isLoggedIn()) {
            checkForWebInquiryForm();
        }
    }
}

async function checkForServicesFormRequest() {
    const formRequest = route.query[UrlQueryKey.ServicesFormRequest];
    if (!formRequest || typeof formRequest !== 'string') {
        return;
    }

    if (formRequest === 'integrations') {
        const customerNo = route.query[UrlQueryKey.CustomerNo];
        const customerName = route.query[UrlQueryKey.CustomerName];

        if (!customerNo || !customerName || typeof customerNo !== 'string' || typeof customerName !== 'string') {
            loggingService.warn('Missing customer information for integrations form request');
            return;
        }

        await formRequestOverlayService.show({
            formType: FormRequestType.integrations,
            customerNo,
            customerName,
        });
    }

    const cleanedQuery = { ...route.query };
    delete cleanedQuery[UrlQueryKey.ServicesFormRequest];
    delete cleanedQuery[UrlQueryKey.CustomerNo];
    delete cleanedQuery[UrlQueryKey.CustomerName];
    router.replace({ query: cleanedQuery }).catch(() => {});
}

watch(
    () => route.query,
    () => {
        clearTimeout(pendingTimer);
        checkForWebInquiryForm();
        checkForServicesFormRequest();
    },
    { immediate: true },
);

onUnmounted(() => {
    clearTimeout(pendingTimer);
});
</script>
